import { createContext, useContext, useEffect, useMemo, useState, useCallback } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { devWarn } from "@/lib/log";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = checking, false = guest, object = authed
  const [loading, setLoading] = useState(true);

  // Deps intentionally empty — uses only stable references (api module, setUser, setLoading).
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
    } catch (e) {
      // Expected when not logged in — surface only unexpected errors.
      if (e?.response?.status !== 401) devWarn("auth/me failed:", e?.message || e);
      setUser(false);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const login = async (email, password, rememberMe = true) => {
    try {
      const { data } = await api.post("/auth/login", { email, password, remember_me: rememberMe });
      setUser(data);
      return { ok: true };
    } catch (e) {
      return { ok: false, error: formatApiErrorDetail(e.response?.data?.detail) || e.message };
    }
  };

  const register = async (name, email, password) => {
    try {
      const { data } = await api.post("/auth/register", { name, email, password });
      setUser(data);
      return { ok: true };
    } catch (e) {
      return { ok: false, error: formatApiErrorDetail(e.response?.data?.detail) || e.message };
    }
  };

  const logout = async () => {
    try { await api.post("/auth/logout"); }
    catch (e) { devWarn("logout call failed:", e?.message || e); }
    setUser(false);
  };

  const hasModule = useCallback((key) => {
    if (!user) return false;
    // Admins AND Managers always see every module — matches the backend
    // ``_user_has_module`` shortcut so the sidebar can't drift from the
    // real API permissions. Only the /admin (User Settings) page is
    // still hidden from managers via an explicit `role === 'admin'`
    // check in `AppLayout`.
    if (user.role === "admin" || user.role === "manager") return true;
    const eff = user.effective_modules;
    if (eff === "*") return true;                    // server-side wildcard (admin)
    if (!key) return true;                           // no gate specified → allow
    return Array.isArray(eff) && eff.includes(key);  // locked by default: absent → deny
  }, [user]);

  // Convenience flag: admin OR manager. Used by pages/components that
  // still branch on "privileged" behaviour (delete buttons, team-wide
  // views, etc.) without leaking through to /admin.
  const isPrivileged = !!(user && (user.role === "admin" || user.role === "manager"));

  const value = useMemo(
    () => ({ user, loading, login, register, logout, refresh, hasModule, isPrivileged }),
    // login/register/logout are closures over setUser; refresh is stable via useCallback
    [user, loading, refresh, hasModule, isPrivileged]
  );

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
